<template>
    <div
        class="toast-container-wrapper fixed top-0 right-4 left-4 z-50"
        :class="toasts.length > 0 ? 'block' : 'hidden'"
        :style="{ paddingTop: safeZoneTop }"
        ref="toastContainer"
        @scroll="checkDismissBreakpoint"
        @touchend="checkDismissBreakpoint"
        @mouseup="checkDismissBreakpoint"
    >
        <div class="toast-list bg-blue flex flex-col gap-4 pt-4">
            <div
                v-for="toast in toasts"
                :key="toast.id"
                :id="`toast-${toast.id}`"
                class="toast-item relative border-2 border-zinc-700 bg-zinc-900 p-4 text-white"
                :class="{ removing: toast.removing }"
            >
                <div class="flex items-center gap-4">
                    <img v-avatar-fallback="toast.image" class="h-15" v-if="toast.image" />
                    <div class="flex flex-1 flex-col justify-center gap-1">
                        <h3 class="font-title text-lg font-bold uppercase">{{ toast.message }}</h3>
                        <p class="text-sm" v-if="toast.subtitle" v-html="toast.subtitle"></p>
                    </div>
                    <DiagonalCutButton
                        v-if="toast.button"
                        @click="handleButtonClick(toast)"
                        variant="primary"
                        cut-position="right-top"
                        :full-width="false"
                        :loading="toast.button.loading"
                        :disabled="toast.button.disabled"
                    >
                        {{ toast.button.label }}
                    </DiagonalCutButton>
                </div>

                <div
                    class="mx-auto mt-2 h-1 w-14 rounded-full bg-zinc-600"
                    @click="removeToast(toast.id)"
                ></div>

                <div
                    class="absolute top-[-2px] right-[-2px] left-[-2px] h-1 overflow-hidden bg-zinc-800"
                    v-if="!toast.removing"
                >
                    <div
                        class="h-full bg-red-500"
                        :style="{
                            width: `${(toast.timeLeft / toast.duration) * 100}%`,
                            transition: 'width 0.05s linear',
                        }"
                    ></div>
                </div>
            </div>
        </div>
        <div class="toast-spacer h-[30vh]"></div>
    </div>
</template>

<script>
import { useToast } from '@/composables/useToast'
import { nextTick } from 'vue'
import DiagonalCutButton from './DiagonalCutButton.vue'

export default {
    name: 'ToastContainer',

    components: {
        DiagonalCutButton,
    },

    data() {
        return {
            isProgrammaticScroll: true,
            resizeObserver: null,
            toasts: [],
            toastId: 0,
            emitter: null,
        }
    },

    computed: {
        safeZoneTop() {
            return (document.safeZone ? document.safeZone.top : 0) + 'px'
        },
    },

    methods: {
        addToast(data) {
            const id = ++this.toastId
            const toast = {
                id,
                message: data.message,
                duration: data.duration,
                subtitle: data.subtitle,
                image: data.image,
                button: data.button,
                timeLeft: data.duration,
                removing: false,
                interval: null,
            }

            this.toasts.push(toast)
            console.log('Toast added:', toast)

            // Auto-scroll to top
            this.isProgrammaticScroll = true
            nextTick(() => {
                if (this.$refs.toastContainer) {
                    this.$refs.toastContainer.scrollTo(0, 0)
                    setTimeout(() => {
                        this.isProgrammaticScroll = false
                    }, 500)
                }
            })

            // Start countdown with setInterval for smooth updates
            const startTime = Date.now()
            const updateTimer = () => {
                const index = this.toasts.findIndex((t) => t.id === id)
                if (index === -1 || this.toasts[index].removing) {
                    clearInterval(toast.interval)
                    return
                }

                const elapsed = Date.now() - startTime
                const newTimeLeft = Math.max(0, data.duration - elapsed)
                this.toasts[index].timeLeft = newTimeLeft

                if (newTimeLeft <= 0) {
                    clearInterval(toast.interval)
                    this.removeToast(id)
                }
            }

            toast.interval = setInterval(updateTimer, 50)

            return id
        },

        removeToast(id) {
            const toast = this.toasts.find((t) => t.id === id)
            if (toast && !toast.removing) {
                toast.removing = true
                console.log('Toast removing:', id)

                if (toast.interval) {
                    clearInterval(toast.interval)
                    toast.interval = null
                }

                setTimeout(() => {
                    const index = this.toasts.findIndex((t) => t.id === id)
                    if (index > -1) {
                        this.toasts.splice(index, 1)
                        console.log('Toast removed:', id)

                        if (this.toasts.length === 0) {
                            this.isProgrammaticScroll = true
                            nextTick(() => {
                                if (this.$refs.toastContainer) {
                                    this.$refs.toastContainer.scrollTo(0, 0)
                                }
                            })
                        }
                    }
                }, 300)
            }
        },

        handleButtonClick(toast) {
            if (toast.button && typeof toast.button.action === 'function') {
                toast.button.action()
                this.removeToast(toast.id)
            }
        },

        checkDismissBreakpoint() {
            if (!this.isProgrammaticScroll && this.toasts.length > 0) {
                this.toasts.forEach((toast) => {
                    const toastElement = this.$refs.toastContainer?.querySelector(
                        `#toast-${toast.id}`,
                    )
                    if (toastElement) {
                        const rect = toastElement.getBoundingClientRect()
                        const containerRect = this.$refs.toastContainer.getBoundingClientRect()

                        if (rect.top < containerRect.top) {
                            this.isProgrammaticScroll = true
                            this.removeToast(toast.id)
                        }
                    }
                })
            }
        },
    },

    mounted() {
        // Get the emitter from useToast
        const { emitter } = useToast()
        this.emitter = emitter

        // Listen for toast add events
        this.emitter.on('toast:add', this.addToast)

        // Set up resize observer
        if (this.$refs.toastContainer) {
            this.resizeObserver = new ResizeObserver(() => {
                if (this.$refs.toastContainer) {
                    this.$refs.toastContainer.scrollTo(0, 0)
                }
            })
            this.resizeObserver.observe(this.$refs.toastContainer)
        }
    },

    beforeUnmount() {
        // Clean up event listeners
        if (this.emitter) {
            this.emitter.off('toast:add', this.addToast)
        }

        // Clean up resize observer
        if (this.resizeObserver) {
            this.resizeObserver.disconnect()
        }

        // Clean up all intervals
        this.toasts.forEach((toast) => {
            if (toast.interval) {
                clearInterval(toast.interval)
            }
        })
    },
}
</script>

<style scoped>
.toast-container-wrapper {
    height: 30vh;
    overflow-y: auto;
    overscroll-behavior-y: contain;

    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
        display: none;
    }

    /* -ms-overflow-style: none; */
    scrollbar-width: none;
}

.toast-list {
    min-height: min-content;
}

.toast-item {
    animation: slideInFromTop 0.4s cubic-bezier(0.445, 0.05, 0.55, 0.95);
    transform-origin: top;
}

@keyframes slideInFromTop {
    from {
        transform: translateY(-100%) scale(0.9);
        opacity: 0;
    }
    to {
        transform: translateY(0) scale(1);
        opacity: 1;
    }
}

/* Transition for leaving toasts */
.toast-item.removing {
    animation: slideOutToTop 0.3s ease-in forwards;
}

@keyframes slideOutToTop {
    from {
        transform: translateY(0) scale(1);
        opacity: 1;
    }
    to {
        transform: translateY(-100%) scale(0.9);
        opacity: 0;
    }
}
</style>
